import { SINGULAR_EVENTS } from '../constants/singularEventTypes';

/** Pinned CDN build — no npm dependency. */
export const SINGULAR_SDK_URL =
  'https://web-sdk-cdn.singular.net/singular-sdk/1.4.10/singular-sdk.js';

let sdkLoadPromise = null;
let sdkInitialized = false;
let currentSubscriberId = null;

/**
 * Sets the subscriber id attached to every Singular event from now on.
 * Call with `null` on logout so subsequent events stop reporting it.
 * @param {string|null} [subscriberId]
 */
export function setSingularSubscriberId(subscriberId) {
  const next = subscriberId ? String(subscriberId) : null;
  const changed = next !== currentSubscriberId;

  currentSubscriberId = next;

  // Before init the id is passed via SingularConfig.withCustomUserId (see initSingular)
  if (changed && isSingularReady()) {
    syncSingularCustomUserId();
  }
}

/**
 * Keeps the SDK's Custom User ID (customer id) equal to the subscriber id.
 * The SDK persists it, so it is also cleared on logout.
 */
function syncSingularCustomUserId() {
  try {
    if (currentSubscriberId) {
      window.singularSdk.login(currentSubscriberId);
    } else {
      window.singularSdk.logout();
    }
  } catch (error) {
    console.warn('[Singular] custom user id sync failed', error);
  }
}

const hasSingularGlobals = () =>
  typeof window !== 'undefined' &&
  window.singularSdk &&
  typeof window.SingularConfig === 'function';

/**
 * Singular is active only when TBX_SINGULAR_ENABLED is explicitly true
 * and TBX_SINGULAR_SDK_KEY / TBX_SINGULAR_SDK_SECRET / TBX_SINGULAR_BUNDLE_ID exist.
 * Missing / empty / "false" variables must not break the site — always fails safe to false.
 * @returns {boolean}
 */
export function isSingularEnvEnabled() {
  try {
    if (typeof window === 'undefined') return false;

    const value = window.__TBX_ENV__?.TBX_SINGULAR_ENABLED;
    const flagOn =
      value === true ||
      (typeof value === 'string' && value.trim().toLowerCase() === 'true');

    if (!flagOn) return false;

    const { sdkKey, sdkSecret, bundleId } = getSingularEnvCredentials();
    return Boolean(sdkKey && sdkSecret && bundleId);
  } catch {
    return false;
  }
}

/**
 * @returns {{ sdkKey: string|null, sdkSecret: string|null, bundleId: string|null }}
 */
export function getSingularEnvCredentials() {
  try {
    const env = (typeof window !== 'undefined' && window.__TBX_ENV__) || {};
    const normalize = (v) => {
      if (v == null) return null;
      const trimmed = String(v).trim();
      return trimmed || null;
    };

    return {
      sdkKey: normalize(env.TBX_SINGULAR_SDK_KEY),
      sdkSecret: normalize(env.TBX_SINGULAR_SDK_SECRET),
      bundleId: normalize(env.TBX_SINGULAR_BUNDLE_ID),
    };
  } catch {
    return { sdkKey: null, sdkSecret: null, bundleId: null };
  }
}

/**
 * Loads the Singular Web SDK from the CDN (singleton).
 * @returns {Promise<void>}
 */
export function loadSingularSdk() {
  if (hasSingularGlobals()) {
    return Promise.resolve();
  }

  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      `script[data-singular-sdk="true"], script[src="${SINGULAR_SDK_URL}"]`,
    );

    if (existing) {
      if (hasSingularGlobals()) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Singular SDK')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = SINGULAR_SDK_URL;
    script.async = true;
    script.dataset.singularSdk = 'true';
    script.onload = () => resolve();
    script.onerror = () => {
      sdkLoadPromise = null;
      reject(new Error('Failed to load Singular SDK'));
    };
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

/**
 * Initializes Singular once credentials are available.
 * @param {{ sdkKey: string, sdkSecret: string, bundleId: string }} config
 * @returns {Promise<boolean>}
 */
export async function initSingular({ sdkKey, sdkSecret, bundleId }) {
  if (!sdkKey || !sdkSecret || !bundleId) {
    return false;
  }

  if (sdkInitialized && hasSingularGlobals()) {
    return true;
  }

  try {
    await loadSingularSdk();

    if (!hasSingularGlobals()) {
      console.warn('[Singular] SDK globals missing after script load');
      return false;
    }

    // The 3rd argument of SingularConfig is the Web App Bundle ID
    const config = new window.SingularConfig(sdkKey, sdkSecret, bundleId);

    if (currentSubscriberId) {
      config.withCustomUserId(currentSubscriberId);
    }

    window.singularSdk.init(config);
    sdkInitialized = true;

    // The SDK persists the Custom User ID across sessions/reloads: without a
    // subscriber (logged out) a stale id from a previous session must be cleared.
    if (!currentSubscriberId) {
      syncSingularCustomUserId();
    }

    return true;
  } catch (error) {
    console.warn('[Singular] init failed', error);
    return false;
  }
}

export function isSingularReady() {
  return sdkInitialized && hasSingularGlobals();
}

/**
 * Event types are opt-in per client (`settings.<client>.useSingular.events`).
 * Also requires TBX_SINGULAR_ENABLED + credentials. Missing / false = disabled.
 * @param {string} eventName
 * @param {Record<string, boolean>|undefined} eventsConfig
 * @returns {boolean}
 */
export function isSingularEventEnabled(eventName, eventsConfig) {
  return (
    Boolean(eventName) &&
    isSingularEnvEnabled() &&
    eventsConfig?.[eventName] === true
  );
}

/**
 * Reports a custom Singular event only when the client environment enables Singular
 * (TBX_SINGULAR_ENABLED + credentials) and the SDK is ready.
 * Attaches `subscriber_id` for logged-in users on every event, unless
 * the caller already set one explicitly.
 * @param {string} eventName
 * @param {Record<string, unknown>} [attributes]
 */
export function trackSingularEvent(eventName, attributes = {}) {
  if (!eventName || !isSingularEnvEnabled() || !isSingularReady()) {
    return;
  }

  try {
    window.singularSdk.event(eventName, {
      ...attributes,
      subscriber_id: attributes.subscriber_id ?? currentSubscriberId,
    });
  } catch (error) {
    console.warn('[Singular] event failed', eventName, error);
  }
}

/**
 * SPA page visit (required by Singular after the first route change).
 * Init already sends the first page visit — do not call on initial load.
 */
export function trackSingularPageVisit() {
  if (!isSingularEnvEnabled() || !isSingularReady()) {
    return;
  }

  try {
    window.singularSdk.pageVisit();
  } catch (error) {
    console.warn('[Singular] pageVisit failed', error);
  }
}

/**
 * Finds the section matching the current pathname (same logic as AppSectionRouter).
 * @param {string} pathname
 * @param {Array<object>} sectionCollection
 * @param {string} pathCountry
 * @returns {object|null}
 */
export function resolveSectionForPathname(
  pathname,
  sectionCollection,
  pathCountry,
) {
  if (!Array.isArray(sectionCollection) || sectionCollection.length === 0) {
    return null;
  }

  const section = sectionCollection.find(
    (item) => pathCountry + item.url === pathname,
  );

  if (section) {
    return section;
  }

  const homeSection = sectionCollection.find((item) => item.isHome);
  if (
    homeSection &&
    (pathname === pathCountry || pathname === `${pathCountry}/`)
  ) {
    return homeSection;
  }

  return null;
}

export function trackScreenView(attributes = {}) {
  trackSingularEvent(SINGULAR_EVENTS.SCREEN_VIEW, {
    screen_name: attributes.screen_name || window.location.pathname,
    page_url: attributes.page_url || window.location.href,
    page_title: attributes.page_title || document.title,
    slug: attributes.slug ?? window.location.pathname,
    section_name: attributes.section_name ?? null,
    ...attributes,
  });
}

/**
 * Finds the banner item matching the click payload.
 * The item `id` is independent of CTA (content, section, or external link):
 * match by item id, nested content id, or destination url/name.
 * @param {object|string} payload
 * @param {Array<object>} [bannerItems]
 * @returns {object|null}
 */
export function findBannerItem(payload, bannerItems = []) {
  if (!payload || !Array.isArray(bannerItems)) {
    return null;
  }

  return (
    bannerItems.find((item) => {
      if (!item) {
        return false;
      }

      if (typeof payload === 'string') {
        return (
          item.id === payload ||
          item.externalUrl === payload ||
          item.sectionUrl === payload ||
          item.sectionName === payload
        );
      }

      return Boolean(
        (payload.id &&
          (item.id === payload.id || item.content?.id === payload.id)) ||
        (payload.externalUrl && item.externalUrl === payload.externalUrl) ||
        (payload.sectionUrl && item.sectionUrl === payload.sectionUrl) ||
        (payload.sectionName && item.sectionName === payload.sectionName),
      );
    }) || null
  );
}

/**
 * @param {object|string} payload
 * @param {Array<object>} [bannerItems]
 * @returns {number|null}
 */
export function findBannerItemOrder(payload, bannerItems = []) {
  return findBannerItem(payload, bannerItems)?.order ?? null;
}

/**
 * CTA destination of a banner click: content, section, or an external link.
 * @param {object|string} payload
 * @param {Record<string, unknown>} [extra]
 * @returns {'content'|'section'|'external link'|null}
 */
export function resolveBannerClickCta(payload, extra = {}) {
  if (extra.cta) {
    return extra.cta;
  }

  if (extra.redirect_type === 'externalUrl' || extra.action === 'externalUrl') {
    return 'external link';
  }

  if (extra.redirect_type === 'sectionUrl' || extra.action === 'sectionUrl') {
    return 'section';
  }

  if (extra.action === 'select_content' || extra.action === 'play') {
    return 'content';
  }

  if (payload && typeof payload === 'object') {
    if (payload.externalUrl) {
      return 'external link';
    }
    if (payload.sectionUrl) {
      return 'section';
    }
    if (payload.id || payload.contentType) {
      return 'content';
    }
  }

  return null;
}

/**
 * Normalizes banner/content payloads into banner_click attributes.
 * @param {object|string} payload
 * @param {Record<string, unknown>} [extra]
 * @param {Array<object>} [bannerItems] banner items from the component's /items endpoint, used to resolve `order`
 */
export function buildBannerClickAttributes(
  payload,
  extra = {},
  bannerItems = [],
) {
  const bannerItem = findBannerItem(payload, bannerItems);
  const content = payload;
  const cta = resolveBannerClickCta(payload, extra);

  return {
    banner_id: bannerItem?.id ?? null,
    content_id: content?.id || bannerItem?.content?.id || null,
    banner_item_name: bannerItem?.title || null,
    content_type: content?.contentType || null,
    item_position: bannerItem?.order ?? content?.order ?? 0,
    ...extra,
    cta,
  };
}

/**
 * Tracks banner_click, but only when the event is enabled for the client.
 * The payload -> banner item order matching is resolved here, so callers
 * only need to pass the raw click payload and the widget's banner items.
 * @param {object|string} payload
 * @param {Record<string, unknown>} [extra]
 * @param {{ eventsConfig?: Record<string, boolean>, bannerItems?: Array<object> }} [options]
 */
export function trackBannerClick(payload, extra = {}, options = {}) {
  const { eventsConfig, bannerItems = [] } = options;

  if (!isSingularEventEnabled(SINGULAR_EVENTS.BANNER_CLICK, eventsConfig)) {
    return;
  }

  trackSingularEvent(
    SINGULAR_EVENTS.BANNER_CLICK,
    buildBannerClickAttributes(payload, extra, bannerItems),
  );
}

/**
 * Normalizes the shared content into content_share attributes.
 * @param {string} shareMethod social network used to share (whatsapp, x, ...)
 * @param {object} [content]
 * @param {Record<string, unknown>} [extra]
 */
export function buildContentShareAttributes(shareMethod, content, extra = {}) {
  const sharedContent = content && typeof content === 'object' ? content : null;

  return {
    subscriber_id: extra.subscriber_id || null,
    content_name: sharedContent?.title || null,
    content_type: sharedContent?.contentType || null,
    share_method: shareMethod || null,
    ...extra,
  };
}

export function trackContentShare(shareMethod, content, extra = {}) {
  trackSingularEvent(
    SINGULAR_EVENTS.CONTENT_SHARE,
    buildContentShareAttributes(shareMethod, content, extra),
  );
}

/**
 * Normalizes the selected profile into profile_view attributes.
 * @param {object} [profile] profile object from the profiles collection
 * @param {Record<string, unknown>} [extra]
 */
export function buildProfileViewAttributes(profile, extra = {}) {
  const selectedProfile =
    profile && typeof profile === 'object' ? profile : null;

  return {
    subscriber_id: extra.subscriber_id || null,
    profile_id: selectedProfile?.id || extra.profile_id || null,
    profile_name: selectedProfile?.name || null,
    screen_name: window.location.pathname,
    ...extra,
  };
}

/**
 * @param {object} [profile]
 * @param {Record<string, unknown>} [extra]
 * @param {Record<string, boolean>|undefined} [eventsConfig] client's useSingular.events
 */
export function trackProfileView(profile, extra = {}, eventsConfig) {
  if (!isSingularEventEnabled(SINGULAR_EVENTS.PROFILE_VIEW, eventsConfig)) {
    return;
  }

  trackSingularEvent(
    SINGULAR_EVENTS.PROFILE_VIEW,
    buildProfileViewAttributes(profile, extra),
  );
}

/**
 * Normalizes an avatar selection into avatar_selected attributes.
 * @param {string|number} avatarId
 * @param {Record<string, unknown>} [extra]
 */
export function buildAvatarSelectedAttributes(avatarId, extra = {}) {
  return {
    subscriber_id: extra.subscriber_id || null,
    profile_id: extra.profile_id || null,
    profile_name: extra.profile_name || null,
    avatar_id: avatarId ?? extra.avatar_id ?? null,
    ...extra,
  };
}

/**
 * @param {string|number} avatarId
 * @param {Record<string, unknown>} [extra]
 * @param {Record<string, boolean>|undefined} [eventsConfig] client's useSingular.events
 */
export function trackAvatarSelected(avatarId, extra = {}, eventsConfig) {
  if (!isSingularEventEnabled(SINGULAR_EVENTS.AVATAR_SELECTED, eventsConfig)) {
    return;
  }

  trackSingularEvent(
    SINGULAR_EVENTS.AVATAR_SELECTED,
    buildAvatarSelectedAttributes(avatarId, extra),
  );
}

/**
 * Normalizes the content instantiated in the player into content_click attributes.
 * Fires once per content, from the single place where the player is
 * instantiated, regardless of which widget (banner, carousel, EPG, ...)
 * triggered the navigation.
 * @param {object} [content]
 * @param {Record<string, unknown>} [extra]
 */
export function buildContentClickAttributes(content, extra = {}) {
  return {
    subscriber_id: extra.subscriber_id || null,
    content_id: content?.id || null,
    content_name: content?.title || null,
    content_type: content?.contentType || null,
    action: extra.action || 'play',
    ...extra,
  };
}

export function trackContentClick(content, extra = {}) {
  trackSingularEvent(
    SINGULAR_EVENTS.CONTENT_CLICK,
    buildContentClickAttributes(content, extra),
  );
}

/**
 * Normalizes a CS-011 occurrence (no access / paywall) into
 * paywall_view / paywall_cta_click attributes (both events share the same shape).
 * `section_name` identifies where the paywall/CTA fired (epg,
 * player, content_overview, content_overview_live).
 * @param {Record<string, unknown>} [extra]
 */
export function buildPaywallAttributes(extra = {}) {
  const { title, ...rest } = extra;

  return {
    ...rest,
    subscriber_id: extra.subscriber_id || null,
    content_id: extra.content_id || null,
    content_name: extra.content_name || title || null,
    content_type: extra.content_type || null,
    urn: extra.urn || null,
    section_name: extra.section_name || null,
    error_code: extra.error_code || 'CS-011',
  };
}

export function trackPaywallView(extra = {}) {
  trackSingularEvent(
    SINGULAR_EVENTS.PAYWALL_VIEW,
    buildPaywallAttributes(extra),
  );
}

/**
 * Tracks paywall_cta_click, but only when the event is enabled for the client.
 * @param {Record<string, unknown>} [extra]
 * @param {Record<string, boolean>|undefined} [eventsConfig] client's useSingular.events
 */
export function trackPaywallCtaClick(extra = {}, eventsConfig) {
  if (
    !isSingularEventEnabled(SINGULAR_EVENTS.PAYWALL_CTA_CLICK, eventsConfig)
  ) {
    return;
  }

  trackSingularEvent(
    SINGULAR_EVENTS.PAYWALL_CTA_CLICK,
    buildPaywallAttributes(extra),
  );
}
